import React from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { NewTabHint, arrowGlyph, newTabRel } from './linkParts';

/**
 * Variants (square, 1px border, flat offset shadow). `arrow` = the square arrow block.
 * - primary:   navy fill, sheet text (15.68:1), orange arrow block — the one main action
 * - secondary: sheet fill, navy border and text, pale rule arrow block
 * - inverse:   paper fill, navy text, orange arrow block — the main action on navy
 * - ghost:     transparent, sheet text and a 50% sheet border — the second action on navy
 */
export const BUTTON_VARIANTS = {
  primary: {
    body: 'border-navy bg-navy text-brand-sheet shadow-site',
    hover: 'hover:shadow-site-hover',
    arrow: 'bg-brand-orange text-brand-sheet',
  },
  secondary: {
    body: 'border-navy bg-brand-sheet text-navy shadow-site',
    hover: 'hover:shadow-site-hover',
    arrow: 'bg-brand-rule/60 text-navy',
  },
  inverse: {
    body: 'border-brand-paper bg-brand-paper text-navy shadow-site',
    hover: 'hover:shadow-site-hover',
    arrow: 'bg-brand-orange text-brand-sheet',
  },
  ghost: {
    body: 'border-brand-sheet/50 bg-transparent text-brand-sheet',
    hover: 'hover:bg-brand-sheet/10',
    arrow: 'bg-brand-orange text-brand-sheet',
  },
};

/** md = the 50/52px page button (13px → 14px text); sm = 44px (12px text), e.g. inside cards. */
export const BUTTON_SIZES = {
  md: {
    // Enlarged text on a phone (under 20em): half the gap, so the wrapping label keeps more room.
    withArrow: 'min-h-[3.125rem] gap-4 py-2 pl-3.5 pr-2.5 text-[0.8125rem] nav:min-h-[3.25rem] nav:gap-7 nav:pl-5 nav:text-sm [@media(max-width:19.9375em)]:gap-2',
    noArrow: 'min-h-[3.125rem] px-5 py-2 text-[0.8125rem] nav:min-h-[3.25rem] nav:text-sm',
    arrow: 'size-[1.8125rem] text-[1.25rem] nav:size-8 nav:text-[1.3125rem]',
  },
  sm: {
    withArrow: 'min-h-11 gap-2 py-1.5 pl-3 pr-2 text-xs',
    noArrow: 'min-h-11 px-4 py-1.5 text-xs',
    arrow: 'size-[1.6875rem] text-base',
  },
};

/**
 * The redesign button. Renders a react-router `Link` when `to` is given, an `<a>` when
 * `href` is given, a `<button>` otherwise (use links for navigation, buttons for actions).
 *
 * Props:
 * - `to` | `href`: destination (router path | URL); neither = a button (`type` default 'button')
 * - `variant`: 'primary' (default) | 'secondary' | 'inverse' | 'ghost' (see BUTTON_VARIANTS)
 * - `size`: 'md' (default) | 'sm'
 * - `arrow`: true (default: the square arrow block, its glyph chosen by the destination —
 *   '→' for same-site navigation and in-page actions: a router `to`, a same-origin `href`,
 *   a `<button>` (a dialog, a form step); '↗' only for what opens outside the site: an
 *   `href` to another origin, `mailto:`, maps; no block for `tel:` / `sms:`, which hand off
 *   to the phone) | false (no block) | a glyph/node that overrides the rule (e.g. '↺'
 *   retry, '↓' in-page jump). Always decorative (aria-hidden). See linkParts arrowGlyph.
 * - `fullWidth`: stretch to the container
 * - `disabled`: a disabled `<button>`; on a link it renders plain text with
 *   aria-disabled (a disabled link cannot be followed)
 * - `loading`: busy state — aria-busy + aria-disabled (keeps focus), clicks and form
 *   submits are ignored; `loadingLabel` replaces the visible text while loading
 * - `target`, `rel`: with target="_blank" the rel gains "noopener noreferrer" and an
 *   sr-only `newTabLabel` (default "(opens in a new tab)") is appended
 * - `onClick`, `className`, `children`, `ref` and any other prop pass through.
 *
 * Hover lifts 2px (only when motion is allowed) and deepens the shadow; focus uses the
 * global brand ring (styles in index.css / site.css).
 */
const Button = React.forwardRef(function Button(
  {
    to,
    href,
    variant = 'primary',
    size = 'md',
    arrow = true,
    fullWidth = false,
    disabled = false,
    loading = false,
    loadingLabel,
    type = 'button',
    target,
    rel,
    newTabLabel,
    onClick,
    className,
    children,
    ...rest
  },
  ref,
) {
  const v = BUTTON_VARIANTS[variant] || BUTTON_VARIANTS.primary;
  const s = BUTTON_SIZES[size] || BUTTON_SIZES.md;
  const glyph = arrowGlyph(arrow, { to, href });
  const hasArrow = glyph !== null;
  const inert = disabled || loading;

  const classes = cn(
    'group inline-flex max-w-full items-center rounded-none border text-left font-body font-semibold no-underline',
    'transition-[transform,box-shadow,background-color,color] duration-180 ease-out',
    hasArrow ? cn('justify-between', s.withArrow) : cn('justify-center text-center', s.noArrow),
    // After the size classes: in cn() a text-[size] after a leading-* drops the leading
    // (tailwind-merge). A `className` that sets a text size must restate its leading.
    'leading-tight',
    v.body,
    inert ? 'cursor-default opacity-50 shadow-none' : cn(v.hover, 'motion-safe:hover:-translate-y-0.5'),
    fullWidth && 'w-full',
    className,
  );

  const content = (
    <>
      {/* The label shrinks and wraps beside the arrow block (which never shrinks): a word
          too long for the line breaks inside the label (hyphenated when the text is
          enlarged on a phone), so it never runs under the arrow. */}
      <span className="min-w-0 break-words [@media(max-width:19.9375em)]:hyphens-auto">
        {loading && loadingLabel ? loadingLabel : children}
      </span>
      {hasArrow ? (
        <span
          aria-hidden="true"
          className={cn(
            'grid shrink-0 place-items-center font-body font-normal leading-none transition-transform duration-180 ease-out',
            s.arrow,
            v.arrow,
            !inert && 'motion-safe:group-hover:rotate-[5deg]',
          )}
        >
          {loading ? '…' : glyph}
        </span>
      ) : null}
    </>
  );

  const isLink = to !== undefined || href !== undefined;

  if (isLink && disabled) {
    return (
      <span ref={ref} aria-disabled="true" className={classes} {...rest}>
        {content}
      </span>
    );
  }

  if (isLink) {
    const linkRel = newTabRel(target, rel);
    const guard = loading
      ? (e) => {
        e.preventDefault();
      }
      : onClick;
    const common = {
      ref,
      className: classes,
      target,
      rel: linkRel,
      onClick: guard,
      'aria-busy': loading || undefined,
      'aria-disabled': loading || undefined,
      ...rest,
    };
    const inner = (
      <>
        {content}
        <NewTabHint target={target} label={newTabLabel} />
      </>
    );
    if (to !== undefined) {
      return (
        <Link to={to} {...common}>
          {inner}
        </Link>
      );
    }
    return (
      <a href={href} {...common}>
        {inner}
      </a>
    );
  }

  const handleClick = (e) => {
    if (loading) {
      e.preventDefault();
      return;
    }
    if (onClick) onClick(e);
  };

  return (
    <button
      ref={ref}
      // eslint-disable-next-line react/button-has-type
      type={type}
      disabled={disabled}
      aria-busy={loading || undefined}
      aria-disabled={loading || undefined}
      onClick={handleClick}
      className={classes}
      {...rest}
    >
      {content}
    </button>
  );
});

export default Button;
